import React from "react";
import { GridCellProps } from "@progress/kendo-react-grid";
import { Badge, GridTools, Icon } from "@axum/ax-design";
import { formatMoney, isBudget, orderDateParts, orderOriginBadge, orderStateBadge } from "../../../utils/orderDisplay";

export interface OrderCellProps extends GridCellProps {
  label?: string;
}

const valueOf = (props: OrderCellProps) => (props.field ? props.dataItem[props.field] : undefined);

const cellAttributes = (props: OrderCellProps, extraClass?: string) => ({
  className: [props.className, extraClass].filter(Boolean).join(" ") || undefined,
  style: props.style,
  "data-label": props.label,
});

export const TextCell = (props: OrderCellProps) => {
  const value = valueOf(props);
  return (
    <td {...cellAttributes(props)}>
      {value !== null && value !== undefined && value !== "" && <span className="ax-table-text ax-zoom">{value}</span>}
    </td>
  );
};

export const NumberCell = (props: OrderCellProps) => (
  <td {...cellAttributes(props, "ax-font-tabular")}>
    <span className="ax-table-text ax-zoom">{valueOf(props)}</span>
  </td>
);

export const MoneyCell = (props: OrderCellProps) => (
  <td {...cellAttributes(props, "ax-font-tabular")}>
    <span className="ax-table-text ax-zoom">{formatMoney(valueOf(props))}</span>
  </td>
);

export const TotalCell = (props: OrderCellProps) => (
  <td {...cellAttributes(props, "ax-font-tabular ax-fw-medium")}>
    <span className="ax-table-text ax-zoom">{formatMoney(valueOf(props))}</span>
  </td>
);

export const DateCell = (props: OrderCellProps) => {
  const parts = orderDateParts(valueOf(props));
  return (
    <td {...cellAttributes(props)}>
      {parts && (
        <span className={`ax-date ax-date-${parts.age} ax-zoom`}>
          <span className="ax-date-absolute">
            <span className="ax-date-day">{parts.day}</span>
            <span className="ax-date-time">{parts.time}</span>
          </span>
          <span className="ax-date-relative">{parts.relative}</span>
        </span>
      )}
    </td>
  );
};

export const OriginCell = (props: OrderCellProps) => {
  const badge = orderOriginBadge(valueOf(props));
  return (
    <td {...cellAttributes(props)}>
      <Badge
        origin={badge.origin}
        variant={badge.origin ? undefined : "neutral"}
        className={`o360-origin-${badge.tone} ax-zoom ax-zoom-center`}
      >
        {badge.label}
      </Badge>
    </td>
  );
};

export const TypeCell = (props: OrderCellProps) => (
  <td {...cellAttributes(props)}>
    <Badge variant={isBudget(props.dataItem) ? "budget" : "order"} className="ax-zoom ax-zoom-center">
      {valueOf(props)}
    </Badge>
  </td>
);

export const StateCell = (props: OrderCellProps) => {
  const badge = orderStateBadge(valueOf(props));
  return (
    <td {...cellAttributes(props)}>
      <Badge variant={badge.variant} icon={badge.icon && <Icon name={badge.icon} />} className={`ax-zoom ax-zoom-center${badge.tone ? ` o360-state-${badge.tone}` : ""}`}>
        {badge.label}
      </Badge>
    </td>
  );
};

const nameOf = (value: string | null | undefined) => (value || "").trim();

export const SellerCell = (props: OrderCellProps) => {
  const name = nameOf(props.dataItem.sellerName);
  return (
    <td {...cellAttributes(props)}>
      <span className="ax-table-text ax-table-text-wrap ax-zoom">
        <span className="ax-text-muted ax-font-tabular">{props.dataItem.sellerId}</span>
        {name && ` · ${name}`}
      </span>
    </td>
  );
};

export const ClientCell = (props: OrderCellProps) => {
  const name = nameOf(props.dataItem.clientName);
  return (
    <td {...cellAttributes(props, "ax-text-start")}>
      <span className="ax-zoom ax-zoom-soft ax-zoom-top">
        <strong className="ax-table-key ax-font-tabular">{props.dataItem.clientId}</strong>
        {name && (
          <>
            <span className="ax-table-key"> · </span>
            <span className="ax-table-key">{name}</span>
          </>
        )}
      </span>
    </td>
  );
};

export const ToolsHeader = () => <GridTools />;

export const ToolsCell = (props: OrderCellProps) => <td className={props.className} />;
